import { useState, type ReactNode } from "react";
import type { DocumentKind, Fidelity } from "@pushback-and-chaos/contracts";
import type { RenderPlan } from "@pushback-and-chaos/documents";
import { DocumentReader } from "../documents/reader.js";
import { tintFor } from "../documents/tints.js";
import plans from "./gallery-plans.generated.json";

/**
 * Dev-only. Every DocumentKind x every Fidelity, rendered by the SAME
 * DocumentReader the product uses, from plans produced by the REAL
 * packages/documents pipeline against the REAL Harborlight documents
 * (scripts/generate-gallery.mjs). No hand-written fixtures — the AGGREGATED
 * roll-ups are arithmetic the transformer did and the CONTESTED pairs are
 * the case's own.
 *
 * Reached at #/gallery in a dev build only; the route is not registered when
 * DEV_PANEL_ENABLED folds to false.
 */
interface GalleryEntry {
  readonly kind: string;
  readonly fidelity: string;
  readonly docId: string;
  readonly plan?: RenderPlan;
  readonly error?: string;
}

const ENTRIES = plans as readonly GalleryEntry[];
const KINDS = [...new Set(ENTRIES.map((e) => e.kind))];
const FIDELITIES = [...new Set(ENTRIES.map((e) => e.fidelity))];

export function DocumentGallery(): ReactNode {
  const [kind, setKind] = useState<string>("FINANCIAL_TABLE");
  const [fidelity, setFidelity] = useState<string>("CONTESTED");
  const [contactSheet, setContactSheet] = useState(false);
  const entry = ENTRIES.find((e) => e.kind === kind && e.fidelity === fidelity);

  return (
    <div className="min-h-svh bg-white">
      <div className="border-b border-b-grid-line-major bg-[#0d0f12] px-6 py-3 font-mono text-[11px] text-[#c8d0d8]">
        <strong className="text-[#7cf]">DEV — document gallery.</strong> {ENTRIES.length} plans, generated by the real
        transform + toRenderPlan against the real Harborlight documents.
        <div className="mt-2 flex flex-wrap gap-1">
          {KINDS.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              className={`cursor-pointer border border-[#2a3038] px-2 py-[2px] font-mono text-[11px] ${
                k === kind ? "bg-[#7cf] text-[#0d0f12]" : "bg-transparent text-[#c8d0d8]"
              }`}
            >
              {k}
            </button>
          ))}
        </div>
        <div className="mt-1 flex flex-wrap gap-1">
          {FIDELITIES.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFidelity(f)}
              className={`cursor-pointer border border-[#2a3038] px-2 py-[2px] font-mono text-[11px] ${
                f === fidelity ? "bg-[#9f9] text-[#0d0f12]" : "bg-transparent text-[#c8d0d8]"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <label className="mt-2 flex cursor-pointer items-center gap-2">
          <input type="checkbox" checked={contactSheet} onChange={(e) => setContactSheet(e.target.checked)} />
          <span>contact sheet — render all {ENTRIES.length} at once (this is what the overflow/leak sweep reads)</span>
        </label>
        <div className="mt-2 text-[#8a929c]">
          {entry?.docId ?? "—"} · tint {tintFor(kind as DocumentKind).fromFrames ? "sampled from a frame" : "PROPOSED (not in the frames)"}
        </div>
      </div>

      {contactSheet ? (
        <div className="px-16 py-10">
          {ENTRIES.map((e) => (
            <section key={`${e.kind}-${e.fidelity}`} data-kind={e.kind} data-fidelity={e.fidelity} className="mb-12">
              <div className="mb-2 font-mono text-[11px] text-ink-soft">
                {e.kind} × {e.fidelity}
              </div>
              {e.plan === undefined ? (
                <pre className="font-mono text-small text-[#b00]">{e.error ?? "no plan"}</pre>
              ) : (
                <div style={{ maxWidth: "62rem" }}>
                  <DocumentReader plan={e.plan} kind={e.kind as DocumentKind} onClose={() => undefined} />
                </div>
              )}
            </section>
          ))}
        </div>
      ) : null}

      <div className={contactSheet ? "hidden" : "px-16 py-10"}>
        {entry === undefined ? (
          <p className="font-mono text-small">no plan for that combination.</p>
        ) : entry.error !== undefined ? (
          <pre className="font-mono text-small text-[#b00]">{entry.error}</pre>
        ) : entry.plan !== undefined ? (
          <div style={{ maxWidth: "62rem" }}>
            <DocumentReader plan={entry.plan} kind={kind as DocumentKind} onClose={() => undefined} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

export type { Fidelity };
